/* OfflineUpgradeDownloadAction.js */

"use strict";

const _ = require("lodash");

const RUN_BUNDLE_STATUS = ["VALIDATED", "BUNDLE_ERROR"];
const RUN_DOWNLOAD_STATUS = ["BUNDLED"];

/**
 * Class representing an action to download the offline upgrade bundle for IoT devices.
 *
 * OfflineUpgradeDownloadAction is responsible for:
 *  - Initiates the download process for the offline upgrade bundle.
 *  - Provides user feedback overlays during the operation.
 *  - Saves the retrieved bundle as a zip file.
 *  - Handles errors and displays appropriate user messages.
 *
 * @example
 *   const action = new OfflineUpgradeDownloadAction(logger, gameEditor, activeView, userSession, modalUserFeedback);
 *   await action.run();
 */
class OfflineUpgradeDownloadAction {
    /**
     * Constructs an OfflineUpgradeDownloadAction.
     * @param {Object} logger - Logger instance.
     * @param {Object} gameEditor - Reference to the game editor.
     * @param {Object} activeView - UI context for the current active view.
     * @param {Object} userSession - Current user session object.
     * @param {Object} modalUserFeedback - Handler for modal user feedback overlays.
     */
    constructor(logger, gameEditor, activeView, userSession, modalUserFeedback) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeView = activeView;
        this._userSession = userSession;
        this._modalUserFeedback = modalUserFeedback;
        this._orgId = _.get(this._userSession, "activeOrganization.id");
        this._plan = this._gameEditor.getPlan();
        this._planId = this._plan ? this._plan.id : undefined;

        this.run = this.run.bind(this);
    }

    /**
     * Runs the offline upgrade bundle download workflow.
     * Displays loading overlays, ensures preconditions, creates bundle if necessary,
     * and triggers download. Handles and reports errors to the user.
     * @returns {Promise<void>}
     */
    async run() {
        try {
            // Precondition checks
            const planName = _.get(this._plan, "name");

            if (!this._plan) {
                throw new Error("Unknown odmOsUpgradePlan");
            }
            if (!this._planId) {
                throw new Error("Unknown odmOsUpgradePlan ID");
            }
            if (!this._orgId) {
                throw new Error("Unknown organization ID");
            }
            if (
                !RUN_BUNDLE_STATUS.includes(this._plan.status) &&
                !RUN_DOWNLOAD_STATUS.includes(this._plan.status)
            ) {
                throw new Error(
                    "Invalid odmOsUpgradePlan status for bundling or downloading offline upgrade bundle"
                );
            }

            // Trigger bundle creation if needed
            if (RUN_BUNDLE_STATUS.includes(this._plan.status)) {
                await this._modalUserFeedback.displayLoading({
                    message: "Creating offline upgrade bundle...",
                });
                try {
                    const bundleResults = await this._plan.runUpgradeBundleWork();
                    if (!_.get(bundleResults, "summary.success", false)) {
                        const errorMessage = _.get(
                            bundleResults,
                            "summary.errorMsg",
                            "Failed to create offline upgrade bundle"
                        );
                        const err = new Error(errorMessage);
                        err.errorCode = _.get(bundleResults, "summary.errorCode", "");
                        throw err;
                    }
                } catch (err) {
                    this._logger.error("OfflineUpgradeDownloadAction error:", err.message);
                    this._modalUserFeedback.displayError({
                        errorMsg:
                            err.message ||
                            "Unable to create offline upgrade bundle. Please try again later.",
                        errorCode: err.errorCode || "",
                    });
                    return;
                }
                this._modalUserFeedback.hideDisplayed();
            }

            // Download the bundle if ready
            if (RUN_DOWNLOAD_STATUS.includes(this._plan.status)) {
                const url = `/entityService/downloadFile/${this._orgId}/OdmOsUpgradePlan/${this._plan.id}/bundleFilePath`;

                // Check if file is available
                const response = await fetch(url, {method: "HEAD", credentials: "include"});

                if (!response.ok) {
                    throw new Error(
                        `Failed to download offline upgrade bundle: ${response.statusText}`
                    );
                } else {
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = planName
                        ? `offline-upgrade-${_.kebabCase(planName)}.zip`
                        : "offline-upgrade.zip";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } else {
                throw new Error(
                    "Invalid odmOsUpgradePlan status for downloading offline upgrade bundle"
                );
            }
        } catch (err) {
            this._logger.error("OfflineUpgradeDownloadAction error:", err.message);
            this._modalUserFeedback.displayError({
                errorMsg:
                    err.message ||
                    "Unable to download offline upgrade bundle. Please try again later.",
                errorCode: "",
            });
        }
    }

    /**
     * Cleans up references.
     */
    shutdown() {
        this._logger = undefined;
        this._gameEditor = undefined;
        this._activeView = undefined;
        this._userSession = undefined;
        this._modalUserFeedback = undefined;
        this._orgId = undefined;
        this._plan = undefined;
        this._planId = undefined;
    }
}

module.exports = OfflineUpgradeDownloadAction;

OfflineUpgradeDownloadAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.ui.userFeedback",
];

OfflineUpgradeDownloadAction.$displayDetails = {
    type: "Action",
    name: "OfflineUpgradeDownloadAction",
    icon: "devicemanager.offline_discovery_download",
    shown: false,
    title: "Upgrade offline devices",
    order: 2,
};
